/* ============================================================
   Thema lesen, setzen, umschalten.

   Wie /opt/fragenbuero/frontend/src/lib/theme.ts, mit EINER Festlegung:
   DUNKEL ist die Vorgabe (E25) — und zwar hart, nicht über das System. Grund:
   die Startseite ist eine gekippte Karte, die den ganzen Schirm füllt. Wer
   sie zum ersten Mal sieht, soll sie im dunklen Stil sehen, denn darauf sind
   Bürofarben, Phasenfarben und die Bögen abgestimmt (Farben.dc.html). Wer
   lieber hell arbeitet, schaltet einmal um, und die Wahl bleibt.

   Der Schlüssel `lp.theme` muss mit public/theme.js übereinstimmen — dort
   steht derselbe Name, damit das Thema schon VOR dem ersten Anstrich sitzt
   und beim Laden nichts aufblitzt.

   Die Basiskarte schaltet mit (E19/T11): das Kartenmodul liest `thema` aus
   dem Speicher und tauscht den Stil über `setStyle` + `transformStyle`. Diese
   Datei weiß davon nichts — sie setzt nur die Klasse.
   ============================================================ */
import { create } from "zustand";
import { load, save } from "./storage";

export type Theme = "dark" | "light";

/** Schlüssel in localStorage: `lp.theme`. MUSS mit public/theme.js übereinstimmen. */
export const THEME_KEY = "theme";

export const THEME_STANDARD: Theme = "dark";

/** Die zwei Werte, die auch in public/theme.js und index.html stehen. */
const THEME_COLOR: Record<Theme, string> = { dark: "#070707", light: "#faf7f5" };

export function systemTheme(): Theme {
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  } catch {
    return THEME_STANDARD;
  }
}

export function readTheme(): Theme {
  const t = load<Theme | null>(THEME_KEY, null);
  if (t === "light" || t === "dark") return t;
  return THEME_STANDARD;
}

/** Setzt das Thema am Dokument — und NUR dort; gespeichert wird in setTheme(). */
export function applyTheme(theme: Theme): void {
  const el = document.documentElement;
  el.classList.toggle("dark", theme === "dark");
  el.classList.toggle("light", theme !== "dark");
  // Literale und nicht getComputedStyle: das läse hier noch den alten Wert.
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme]);
}

/* ------------------------------------------------------------------ Speicher
   Ein winziger zustand-Speicher über denselben Werten. Grund: das Thema
   entscheidet nicht nur über eine CSS-Klasse, sondern über den BASISKARTENSTIL
   (E19/T11) — das Kartenmodul muss auf den Wechsel reagieren und `setStyle`
   rufen. Ein Wert, der nur am <html>-Element hängt, löst kein Neuzeichnen aus;
   mit dem Speicher hängen Kopfleiste und Karte an derselben Quelle.

   `applyTheme` bleibt die einzige Stelle, die das Dokument anfasst. */

interface ThemaZustand {
  thema: Theme;
  setzen(t: Theme): void;
  umschalten(): void;
}

const useThemaStore = create<ThemaZustand>((set, get) => ({
  thema: readTheme(),
  setzen(t) {
    applyTheme(t);
    save(THEME_KEY, t);
    set({ thema: t });
  },
  umschalten() {
    get().setzen(get().thema === "dark" ? "light" : "dark");
  },
}));

export function setTheme(theme: Theme): void {
  useThemaStore.getState().setzen(theme);
}

export function toggleTheme(): Theme {
  useThemaStore.getState().umschalten();
  return useThemaStore.getState().thema;
}

/**
 * Der Hook für Kopfleiste und Karte:
 *   const { thema, setzen, umschalten } = useTheme();
 * `thema` ist "dark" oder "light" und ändert sich bei jedem Umschalten —
 * das Kartenmodul hängt seinen `setStyle`-Effekt daran.
 */
export function useTheme(): ThemaZustand {
  return useThemaStore();
}

/** Das Thema außerhalb von React (Kartenaufbau vor dem ersten Anstrich). */
export function aktuellesThema(): Theme {
  return useThemaStore.getState().thema;
}
